<template>
  <q-card class="media-manager column no-wrap">
    <q-bar class="bg-white text-grey-9">
      <q-icon name="perm_media" color="grey-9" />
      <div class="text-weight-bold">Médias — {{ resources.length }} fichier(s)</div>
      <q-space />
      <q-btn dense flat icon="close" v-close-popup><q-tooltip>Fermer</q-tooltip></q-btn>
    </q-bar>

    <div class="row no-wrap col">
      <!-- SIDEBAR DOSSIERS -->
      <div class="sidebar column no-wrap">
        <div class="sidebar-title">Dossiers</div>
        <q-list class="col scroll q-px-sm">
          <q-item clickable :active="selectedFolderId==='all'" active-class="active-folder" class="folder-item" @click="selectedFolderId='all'">
            <q-item-section avatar><q-icon name="collections" size="20px" /></q-item-section>
            <q-item-section>Tous les médias</q-item-section>
            <q-item-section side><span class="count-badge" :class="{ 'count-badge--active': selectedFolderId==='all' }">{{ resources.length }}</span></q-item-section>
          </q-item>
          <q-item clickable :active="selectedFolderId==='none'" active-class="active-folder" class="folder-item" @click="selectedFolderId='none'">
            <q-item-section avatar><q-icon name="folder_off" size="20px" /></q-item-section>
            <q-item-section>Sans dossier</q-item-section>
            <q-item-section side><span class="count-badge" :class="{ 'count-badge--active': selectedFolderId==='none' }">{{ countNoFolder }}</span></q-item-section>
          </q-item>

          <div class="sidebar-sep">Mes dossiers</div>

          <q-item v-for="f in folders" :key="f.id" clickable :active="selectedFolderId===f.id" active-class="active-folder" class="folder-item" @click="selectedFolderId=f.id">
            <q-item-section avatar><span class="folder-dot" :style="{ background: f.color }"></span></q-item-section>
            <q-item-section class="ellipsis">{{ f.name }}</q-item-section>
            <q-item-section side>
              <div class="row items-center no-wrap">
                <span class="count-badge" :class="{ 'count-badge--active': selectedFolderId===f.id }">{{ countByFolder[f.id] || 0 }}</span>
                <q-btn dense flat round size="xs" icon="more_vert" class="folder-menu-btn q-ml-xs" @click.stop>
                  <q-menu>
                    <q-list dense style="min-width:150px">
                      <q-item clickable v-close-popup @click="openRenameFolder(f)"><q-item-section avatar><q-icon name="edit" size="18px" /></q-item-section><q-item-section>Renommer</q-item-section></q-item>
                      <q-item clickable v-close-popup @click="deleteFolder(f)"><q-item-section avatar><q-icon name="delete" size="18px" color="negative" /></q-item-section><q-item-section class="text-negative">Supprimer</q-item-section></q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
            </q-item-section>
          </q-item>

          <div v-if="folders.length === 0" class="sidebar-empty">Aucun dossier pour l'instant.<br>Créez-en un pour ranger vos médias.</div>
        </q-list>
        <q-btn color="secondary" class="q-ma-sm" icon="create_new_folder" label="Nouveau dossier" @click="openNewFolder" />
      </div>

      <!-- MAIN -->
      <div class="main column no-wrap col">
        <div class="row items-center q-pa-sm" style="gap: 8px;">
          <q-input dense outlined v-model="search" placeholder="Rechercher…" class="col" debounce="200">
            <template v-slot:prepend><q-icon name="search" /></template>
          </q-input>
          <q-btn-toggle v-model="typeFilter" dense outline toggle-color="secondary" text-color="grey-7" class="type-filter"
            :options="[{label:'Tout',value:'all'},{label:'Images',value:'image'},{label:'Vidéos',value:'video'},{label:'Sons',value:'audio'},{label:'Docs',value:'doc'}]" />
          <q-btn dense unelevated color="secondary" icon="upload" label="Importer" @click="triggerFileInput" />
          <input ref="fileInput" type="file" multiple hidden
            accept="image/*,video/*,audio/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
            @change="onFilesSelected" />
        </div>

        <div v-if="selectedIds.length" class="row items-center q-px-sm q-py-xs selection-bar" style="gap: 8px;">
          <span class="text-weight-medium text-grey-9">{{ selectedIds.length }} sélectionné(s)</span>
          <q-btn dense flat icon="drive_file_move" label="Déplacer vers">
            <q-menu>
              <q-list dense style="min-width:160px">
                <q-item clickable v-close-popup @click="moveSelected(null)"><q-item-section>Sans dossier</q-item-section></q-item>
                <q-item v-for="f in folders" :key="f.id" clickable v-close-popup @click="moveSelected(f.id)"><q-item-section>{{ f.name }}</q-item-section></q-item>
              </q-list>
            </q-menu>
          </q-btn>
          <q-btn dense flat icon="delete" label="Supprimer" color="negative" @click="deleteSelected" />
          <q-btn dense flat label="Tout désélectionner" @click="selectedIds=[]" />
        </div>

        <div class="grid q-pa-sm col scroll" :class="{ 'drag-over': dragOver }"
          @dragover.prevent="dragOver=true" @dragleave.prevent="dragOver=false" @drop.prevent="onDrop">
          <div class="dropzone" @click="triggerFileInput">
            <q-icon name="cloud_upload" size="28px" />
            <div class="text-caption">Glissez vos fichiers ici · plusieurs à la fois</div>
          </div>
          <div v-for="r in filteredResources" :key="r.id" class="tile" style="align-self:start;overflow:hidden;" :class="{ selected: selectedIds.includes(r.id) }" @click="toggleSelect(r.id)">
            <div class="thumb" style="height:112px;min-height:112px;max-height:112px;overflow:hidden;">
              <img v-if="getMediaType(r.name)==='image' && thumbs[r.name]" :src="thumbs[r.name]" alt="" style="width:100%;height:112px;object-fit:cover;display:block;" />
              <q-icon v-else-if="getMediaType(r.name)==='video'" name="movie" size="34px" color="pink-5" />
              <q-icon v-else-if="getMediaType(r.name)==='audio'" name="music_note" size="34px" color="teal-5" />
              <q-icon v-else-if="getMediaType(r.name)==='doc'" name="description" size="34px" color="orange-6" />
              <q-icon v-else name="insert_drive_file" size="34px" color="grey-6" />
              <div class="check"><q-icon size="20px" :name="selectedIds.includes(r.id) ? 'check_circle' : 'radio_button_unchecked'" /></div>
              <q-btn class="edit-btn" dense flat round size="sm" icon="edit" @click.stop="openEdit(r)" />
            </div>
            <div class="title ellipsis">{{ r.title || r.name }}</div>
          </div>
        </div>
      </div>
    </div>

    <q-inner-loading :showing="uploading">
      <q-spinner-gears size="50px" color="secondary" />
      <div class="q-mt-sm">Import en cours… {{ progress }}%</div>
    </q-inner-loading>

    <q-dialog v-model="folderDialog">
      <q-card style="min-width:320px">
        <q-card-section class="text-h6">{{ editingFolder ? 'Renommer le dossier' : 'Nouveau dossier' }}</q-card-section>
        <q-card-section class="q-gutter-sm">
          <q-input dense autofocus v-model="folderName" label="Nom" />
          <q-input dense v-model="folderColor" label="Couleur">
            <template v-slot:append>
              <q-icon name="colorize" class="cursor-pointer"><q-popup-proxy><q-color v-model="folderColor" /></q-popup-proxy></q-icon>
            </template>
          </q-input>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Annuler" v-close-popup />
          <q-btn flat color="secondary" label="Enregistrer" @click="saveFolder" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="editDialog">
      <q-card style="min-width:420px">
        <q-card-section class="text-h6">Modifier le média</q-card-section>
        <q-card-section class="q-gutter-sm">
          <q-input dense v-model="editForm.title" label="Titre" />
          <q-input dense v-model="editForm.description" label="Description" />
          <q-input dense v-model="editForm.source" label="Source" />
          <q-input dense v-model="editForm.source_url" label="URL de la source" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Annuler" v-close-popup />
          <q-btn flat color="secondary" label="Enregistrer" @click="saveMetadata" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from 'vue';
import axios from 'axios';
import { getMediaType } from '../utils/mediaType';

const props = defineProps({ exhibitId: { required: true, default: null } });
const BASE = process.env.VUE_APP_BASEURL;

const folders = ref([]);
const resources = ref([]);
const thumbs = ref({});
const selectedFolderId = ref('all');
const search = ref('');
const typeFilter = ref('all');
const selectedIds = ref([]);
const dragOver = ref(false);
const uploading = ref(false);
const progress = ref(0);
const fileInput = ref(null);

const folderDialog = ref(false);
const editingFolder = ref(null);
const folderName = ref('');
const folderColor = ref('#8257d4');

const editDialog = ref(false);
const editForm = ref({ id: null, name: '', title: '', description: '', source: '', source_url: '' });

const countNoFolder = computed(() => resources.value.filter(r => !r.folder_id).length);
const countByFolder = computed(() => {
  const m = {};
  for (const r of resources.value) if (r.folder_id) m[r.folder_id] = (m[r.folder_id] || 0) + 1;
  return m;
});
const filteredResources = computed(() => resources.value.filter(r => {
  if (selectedFolderId.value === 'none' && r.folder_id) return false;
  if (selectedFolderId.value !== 'all' && selectedFolderId.value !== 'none' && r.folder_id !== selectedFolderId.value) return false;
  if (typeFilter.value !== 'all' && getMediaType(r.name) !== typeFilter.value) return false;
  if (search.value) {
    const q = search.value.toLowerCase();
    if (!(`${r.title || ''} ${r.name || ''}`.toLowerCase().includes(q))) return false;
  }
  return true;
}));

const fetchFolders = async () => {
  try { folders.value = (await axios.get(`${BASE}/folders/${props.exhibitId}`)).data; }
  catch (e) { console.error(e); }
};
const fetchResources = async () => {
  try {
    const { data } = await axios.post(`${BASE}/resources`, { exhibitId: props.exhibitId });
    resources.value = data;
    for (const r of data) {
      if (getMediaType(r.name) === 'image' && !thumbs.value[r.name]) {
        axios.get(`${BASE}/s3Resource/${r.name}`).then(res => { thumbs.value[r.name] = res.data; }).catch(() => {});
      }
    }
  } catch (e) { console.error(e); }
};

const toggleSelect = (id) => {
  const i = selectedIds.value.indexOf(id);
  if (i === -1) selectedIds.value.push(id); else selectedIds.value.splice(i, 1);
};

const triggerFileInput = () => fileInput.value && fileInput.value.click();
const onFilesSelected = (e) => { uploadFiles(Array.from(e.target.files)); e.target.value = ''; };
const onDrop = (e) => { dragOver.value = false; uploadFiles(Array.from(e.dataTransfer.files)); };

const uploadFiles = async (files) => {
  if (!files.length) return;
  uploading.value = true; progress.value = 0;
  try {
    const form = new FormData();
    form.append('exhibitId', props.exhibitId);
    for (const f of files) form.append('files', f); // déposés => "Sans dossier" (folderId omis)
    await axios.post(`${BASE}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => { if (e.total) progress.value = Math.round(e.loaded * 100 / e.total); }
    });
    await fetchResources();
  } catch (err) { console.error(err); }
  finally { uploading.value = false; }
};

const moveSelected = async (folderId) => {
  try {
    await axios.put(`${BASE}/resources/folder`, { exhibitId: props.exhibitId, resourceIds: selectedIds.value, folderId });
    selectedIds.value = [];
    await fetchResources();
  } catch (e) { console.error(e); }
};

const deleteSelected = async () => {
  if (!confirm(`Supprimer ${selectedIds.value.length} média(s) ?`)) return;
  const toDelete = resources.value.filter(r => selectedIds.value.includes(r.id));
  for (const r of toDelete) {
    try { await axios.delete(`${BASE}/file`, { data: { name: r.name, exhibitId: props.exhibitId, resourceId: r.id } }); }
    catch (e) { console.error(e); }
  }
  selectedIds.value = [];
  await fetchResources();
};

const openNewFolder = () => { editingFolder.value = null; folderName.value = ''; folderColor.value = '#8257d4'; folderDialog.value = true; };
const openRenameFolder = (f) => { editingFolder.value = f; folderName.value = f.name; folderColor.value = f.color || '#8257d4'; folderDialog.value = true; };
const saveFolder = async () => {
  if (!folderName.value) return;
  try {
    if (editingFolder.value) await axios.put(`${BASE}/folder/${editingFolder.value.id}`, { name: folderName.value, color: folderColor.value });
    else await axios.post(`${BASE}/folder`, { exhibitId: props.exhibitId, name: folderName.value, color: folderColor.value });
    folderDialog.value = false;
    await fetchFolders();
  } catch (e) { console.error(e); }
};
const deleteFolder = async (f) => {
  if (!confirm(`Supprimer le dossier « ${f.name} » ? Les médias ne seront pas supprimés.`)) return;
  try {
    await axios.delete(`${BASE}/folder/${f.id}`);
    if (selectedFolderId.value === f.id) selectedFolderId.value = 'all';
    await fetchFolders(); await fetchResources();
  } catch (e) { console.error(e); }
};

const openEdit = (r) => { editForm.value = { id: r.id, name: r.name, title: r.title || '', description: r.description || '', source: r.source || '', source_url: r.source_url || '' }; editDialog.value = true; };
const saveMetadata = async () => {
  try {
    await axios.put(`${BASE}/resourceData`, {
      name: editForm.value.name, ancientName: editForm.value.name,
      description: editForm.value.description, title: editForm.value.title,
      source: editForm.value.source, urlSource: editForm.value.source_url,
      resourceId: editForm.value.id
    });
    editDialog.value = false;
    await fetchResources();
  } catch (e) { console.error(e); }
};

onMounted(async () => { await fetchFolders(); await fetchResources(); });
</script>

<style scoped>
.media-manager { height: 100%; }
.sidebar { width: 248px; flex-shrink: 0; background: #fafafa; border-right: 1px solid #e0e0e0; }
.sidebar-title { font-size: .72rem; font-weight: 700; letter-spacing: .08em; color: #9e9e9e; text-transform: uppercase; padding: 14px 16px 6px; }
.sidebar-sep { font-size: .65rem; font-weight: 700; letter-spacing: .08em; color: #bdbdbd; text-transform: uppercase; padding: 14px 12px 4px; }
.folder-item { border-radius: 8px; margin-bottom: 2px; min-height: 42px; color: #1d1d1f; }
.folder-item:hover { background: #f0f0f0; }
.active-folder { background: #e4e7ea; color: #1d1d1f; font-weight: 600; }
.folder-dot { width: 14px; height: 14px; border-radius: 5px; display: inline-block; box-shadow: 0 0 0 1px rgba(0,0,0,.12); }
.count-badge { font-size: .72rem; color: #757575; background: #e8e8e8; border-radius: 10px; padding: 1px 8px; min-width: 22px; text-align: center; display: inline-block; }
.count-badge--active { background: #cfd4da; color: #1d1d1f; }
.folder-menu-btn { color: #9e9e9e; }
.sidebar-empty { font-size: .8rem; color: #bdbdbd; text-align: center; padding: 18px 14px; line-height: 1.5; }
.selection-bar { background: #f0f0f0; border-bottom: 1px solid #e0e0e0; }
/* .grid porte la classe Quasar .col qui impose min-height:0; max-height:100% —
   ce max-height casse le dimensionnement des lignes de la grille. On le neutralise
   ici (spécificité .grid[data-v] > .col). overflow:auto (classe .scroll) gère le défilement. */
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; align-content: start; min-height: 0; max-height: none; }
.grid.drag-over { outline: 2px dashed #26A69A; outline-offset: -8px; background: rgba(38,166,154,.05); }
.dropzone { grid-column: span 2; border: 2px dashed #bdbdbd; border-radius: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 14px; color: #9e9e9e; cursor: pointer; min-height: 120px; text-align: center; }
.dropzone:hover { border-color: #26A69A; color: #26A69A; }
.tile { align-self: start; border: 1px solid #e0e0e0; border-radius: 10px; overflow: hidden; cursor: pointer; transition: box-shadow .15s, border-color .15s; }
.tile.selected { border-color: #26A69A; box-shadow: 0 0 0 3px rgba(38,166,154,.25); }
.thumb { position: relative; height: 112px; min-height: 112px; max-height: 112px; overflow: hidden; display: flex; align-items: center; justify-content: center; background: #f4f4f5; }
.thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.thumb .check { position: absolute; top: 4px; right: 4px; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; color: #26A69A; background: rgba(255,255,255,.9); border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,.15); }
.edit-btn { position: absolute; bottom: 4px; right: 4px; background: rgba(255,255,255,.9); }
.title { padding: 4px 6px; font-size: .75rem; }
</style>
